import {
  createItemCacheStore,
  IItemCacheStore,
  IItemCacheConfiguration,
  getNextItemsScopeItemKey,
  pruneLastAccessedItems
} from "@ms/items-view-prefetch";

import {
  prefetchSpFolderItemSet,
  spFolderItemSetKeyFacet
} from "@ms/items-view-addon-sp-prefetch";

// Import this from prefetch package when available
import {
  readItems,
  IItemCache,
  Facet,
  resetPageLoad,
  removeItemDemands
} from "@ms/items-view";
import { isInCloudStorage } from "@msteams/bridges-services-utilities-files";
import { ISiteInfo } from "@msteams/bridges-models-files";
import { IFilesPrefetcher } from "./files-prefetcher.interface";
import { createRootItemKey } from "./files-prefetcher-helper";

let itemCacheStore: IItemCacheStore | null;

export function getFilesPrefetcher(
  configuration?: IItemCacheConfiguration
): IFilesPrefetcher {
  if (!itemCacheStore && configuration) {
    itemCacheStore = createItemCacheStore({
      configuration
    });
  }
  return filesPrefetcher;
}

const filesPrefetcher: IFilesPrefetcher = {
  prefetchSharepointData: (siteInfo: ISiteInfo): Promise<void> => {
    if (!itemCacheStore) {
      return Promise.reject();
    }

    // Can happen multiple times
    const inCloudStorage = isInCloudStorage(siteInfo);

    const rootItemKey = createRootItemKey(siteInfo, inCloudStorage);
    return itemCacheStore.dispatch(
      prefetchSpFolderItemSet({
        itemKey: spFolderItemSetKeyFacet.serialize(rootItemKey)
      })
    );
  },
  pruneData: (maxAge: number) => {
    // Prune store with max age
    itemCacheStore &&
      itemCacheStore.dispatch(pruneLastAccessedItems({ maxAge }));
  },
  /**
   * Return the store to be sent to ItemScope when rendering
   * Don't use this store directly other than passing to to ItemScope
   * Instead add a function to the prefetcher for its usage
   */
  getStore: (): IItemCacheStore | null => itemCacheStore,

  getOneDriveItemScopeKey: (facet: Facet<string>, key: string): string => {
    let itemkey = filesPrefetcher.readFromStore(facet, key);
    return itemkey ? itemkey : (itemkey = getNextItemsScopeItemKey());
  },

  getOneDriveOwner: (facet: Facet<{}>, key: string): object => {
    let owner = filesPrefetcher.readFromStore(facet, key);
    return owner ? owner : (owner = {});
  },

  /**
   * @param facet - facet to be retrieved from the store
   * @param key - key used to extract the item
   * @returns unpacked value of the specified facet
   */
  readFromStore: <T>(facet: Facet<T>, key: string): T | null =>
    itemCacheStore &&
    itemCacheStore.dispatch(
      readItems(
        (itemCache: IItemCache) => itemCache.demandItemFacet(facet, key)!!!
      )
    ),
  /**
   * Resets the page load for the store. So the next load for items view will not show
   * any file type icons, columns or layout switcher
   */
  resetPageLoad: () => {
    itemCacheStore && itemCacheStore.dispatch(resetPageLoad());
  },

  /**
   * Removes the permanent flag from all the data present in itemsViewCache under the given owner
   */
  deregisterPermanentDataFromStore: (owner: object): void => {
    itemCacheStore && itemCacheStore.dispatch(removeItemDemands({ owner }));
  },

  /**
   * Deletes the reference to the store so essentially the next time a new store is created
   * and all things are dispatched again. Like request to download bundles, create addons, etc.
   */
  destoryStore: () => {
    itemCacheStore = null;
  }
};
